import React, { useState } from "react";
import { SEOHelmet } from "../../utils/SEOHelmet";
import BackButton from "../../components/BackButton";
import achievementsData from "../../data/achievementsConfig";
import "./css/resumeContact.css";
import "./css/achievements.css";

const Achievements = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const totalDocuments = achievementsData.length;
  const previewReadyCount = achievementsData.filter(
    (documentItem) => documentItem.previewImage,
  ).length;

  const categories = [
    {
      id: "all",
      name: "All Documents",
      count: achievementsData.length,
    },
    ...Array.from(
      new Set(achievementsData.map((documentItem) => documentItem.category)),
    ).map((category) => ({
      id: category.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      name: category,
      count: achievementsData.filter(
        (documentItem) => documentItem.category === category,
      ).length,
    })),
  ];

  const filteredDocuments =
    activeFilter === "all"
      ? achievementsData
      : achievementsData.filter(
          (documentItem) =>
            documentItem.category.toLowerCase().replace(/[^a-z0-9]+/g, "-") ===
            activeFilter,
        );

  const getShortSummary = (documentItem) =>
    documentItem.summary || documentItem.purpose || documentItem.description;

  const truncateText = (text, limit = 130) => {
    if (!text) return "";
    return text.length > limit ? `${text.slice(0, limit).trim()}...` : text;
  };

  const openDetails = (documentItem) => {
    setSelectedDocument(documentItem);
  };

  const closeDetails = () => {
    setSelectedDocument(null);
  };

  const handleCardKeyDown = (event, documentItem) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDetails(documentItem);
    }
  };

  const openPreview = (previewPath) => {
    const previewWindow = window.open(
      previewPath,
      "_blank",
      "noopener,noreferrer",
    );
    if (previewWindow) {
      previewWindow.opener = null;
    }
  };

  const downloadDocument = (documentPath, documentName) => {
    const downloadLink = document.createElement("a");
    downloadLink.href = documentPath;
    downloadLink.download = documentName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <>
      <SEOHelmet
        title="Achievements - Lucky Sharma (itsluckysharma01) | Research Paper & Recommendation"
        description="View Lucky Sharma's achievements, including a research paper on skin disease detection and a letter of recommendation. Preview and download each document from a single organized section."
        keywords="Lucky Sharma achievements, itsluckysharma01 achievements, research paper, letter of recommendation, publication, recommendation letter, portfolio documents"
        url="https://itsluckysharma01.in/achievements"
        type="article"
      />
      <div className="resume-contact achievements-page">
        <BackButton position="top-left" size="medium" />

        <section id="hero" className="achievements-hero">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">
                My <span className="text-gradient">Achievements</span> Gallery
              </h1>
              <p className="hero-description">
                A polished gallery for important documents where the card keeps
                only the essentials and the full story opens in a detail view.
              </p>

              <div className="hero-actions">
                <a href="#documents" className="btn-primary">
                  <span>Explore Gallery</span>
                </a>
                <a href="#collection-summary" className="btn-secondary">
                  <span>View Summary</span>
                </a>
              </div>
            </div>

            <div className="availability-status">
              <div className="card">
                <div className="availability-header">
                  <div className="status-indicator"></div>
                  <span className="status-text">Premium Document Showcase</span>
                </div>
                <p className="availability-description">
                  Built to feel closer to the certificate gallery, with a
                  cleaner visual hierarchy and room for future additions.
                </p>
                <div className="services-grid">
                  <div className="service-item">
                    <div className="service-title primary">Focused Cards</div>
                    <div className="service-subtitle">Title, date, summary</div>
                  </div>
                  <div className="service-item">
                    <div className="service-title secondary">
                      Rich Detail View
                    </div>
                    <div className="service-subtitle">Click to expand</div>
                  </div>
                  <div className="service-item">
                    <div className="service-title accent">Easy to Extend</div>
                    <div className="service-subtitle">Add more docs later</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="collection-summary" className="resume-timeline-section">
          <div className="container">
            <div className="resume-timeline-grid achievements-summary-grid">
              <div className="card">
                <h2 className="section-title">
                  Collection <span className="text-gradient">Summary</span>
                </h2>
                <div className="stats-grid achievements-stats-grid">
                  <div className="stat-item">
                    <div className="stat-number primary">{totalDocuments}</div>
                    <div className="stat-label">Documents</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number secondary">
                      {previewReadyCount}
                    </div>
                    <div className="stat-label">Preview Ready</div>
                  </div>
                </div>
                <p className="summary-copy">
                  This collection highlights verified publications,
                  recommendations, and technical accomplishments earned through
                  research, innovation, and industry experience.
                </p>
              </div>

              <div className="card">
                <h2 className="section-title">
                  Document <span className="text-gradient">Workflow</span>
                </h2>
                <div className="workflow-list">
                  <div className="workflow-step">
                    <span className="workflow-index">01</span>
                    <div>
                      <h3>Scan the summary</h3>
                      <p>Read only the compact visible details on the card.</p>
                    </div>
                  </div>
                  <div className="workflow-step">
                    <span className="workflow-index">02</span>
                    <div>
                      <h3>Open full details</h3>
                      <p>
                        Click the card to see purpose, intro, and highlights.
                      </p>
                    </div>
                  </div>
                  <div className="workflow-step">
                    <span className="workflow-index">03</span>
                    <div>
                      <h3>Preview or download</h3>
                      <p>Use the detail modal for the full file and context.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="documents" className="documents-section">
          <div className="container">
            <div className="documents-header">
              <h2 className="section-title">
                Featured <span className="text-gradient">Documents</span>
              </h2>
              <p className="documents-description">
                Each card shows only the essentials. Open a document to see the
                full purpose, organization, introduction, and highlights.
              </p>
            </div>

            <div
              className="document-filters"
              role="tablist"
              aria-label="Achievements categories"
            >
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`filter-button ${activeFilter === category.id ? "active" : ""}`}
                  onClick={() => setActiveFilter(category.id)}
                >
                  <span className="filter-text">{category.name}</span>
                  <span className="filter-count">{category.count}</span>
                </button>
              ))}
            </div>

            <div className="documents-grid">
              {filteredDocuments.map((documentItem) => (
                <article
                  key={documentItem.id}
                  className="document-card card"
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetails(documentItem)}
                  onKeyDown={(event) => handleCardKeyDown(event, documentItem)}
                >
                  <div className="document-preview-wrap">
                    <div className="document-preview-button">
                      <img
                        src={documentItem.previewImage}
                        alt={documentItem.previewAlt}
                        className="document-preview-image"
                        loading="lazy"
                      />
                      <div className="document-preview-overlay">
                        <span>View Details</span>
                      </div>
                    </div>
                    <div className="document-badge-row">
                      <span className={`document-badge ${documentItem.accent}`}>
                        {documentItem.category}
                      </span>
                      <span className="document-format">
                        {documentItem.format}
                      </span>
                    </div>
                  </div>

                  <div className="document-body">
                    <div className="document-header">
                      <h3 className="document-title">{documentItem.title}</h3>
                      <span className="document-level">
                        {documentItem.level}
                      </span>
                    </div>
                    <p className="document-description">
                      {truncateText(getShortSummary(documentItem), 150)}
                    </p>

                    <div className="document-meta-list">
                      <div className="document-meta-item">
                        <span>Organization</span>
                        <p>{truncateText(documentItem.organization, 72)}</p>
                      </div>
                      <div className="document-meta-item">
                        <span>Date</span>
                        <p>{documentItem.date}</p>
                      </div>
                    </div>

                    <div className="document-actions document-actions-compact">
                      <button
                        type="button"
                        className="btn-primary flex-1"
                        onClick={(event) => {
                          event.stopPropagation();
                          openDetails(documentItem);
                        }}
                      >
                        <span>View Details</span>
                      </button>
                      <button
                        type="button"
                        className="btn-secondary flex-1"
                        onClick={(event) => {
                          event.stopPropagation();
                          openPreview(documentItem.previewImage);
                        }}
                      >
                        <span>Preview</span>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {selectedDocument ? (
          <div className="document-modal-backdrop" onClick={closeDetails}>
            <div
              className="document-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="document-modal-title"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="document-modal-close"
                onClick={closeDetails}
                aria-label="Close details"
              >
                ×
              </button>

              <div className="document-modal-grid">
                <div className="document-modal-preview">
                  <img
                    src={selectedDocument.previewImage}
                    alt={selectedDocument.previewAlt}
                  />
                </div>

                <div className="document-modal-content">
                  <div className="document-modal-meta-row">
                    <span
                      className={`document-badge ${selectedDocument.accent}`}
                    >
                      {selectedDocument.category}
                    </span>
                    <span className="document-format">
                      {selectedDocument.format}
                    </span>
                    <span className="document-level">
                      {selectedDocument.level}
                    </span>
                  </div>

                  <h3
                    id="document-modal-title"
                    className="document-modal-title"
                  >
                    {selectedDocument.title}
                  </h3>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Purpose</span>
                    <p>{selectedDocument.purpose}</p>
                  </div>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Organization</span>
                    <p>{selectedDocument.organization}</p>
                  </div>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Introduction</span>
                    <p>{selectedDocument.introduction}</p>
                  </div>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Details</span>
                    <p>{selectedDocument.description}</p>
                  </div>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Key Highlights</span>
                    <ul className="document-modal-list">
                      {selectedDocument.highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="document-modal-section">
                    <span className="document-modal-label">Issuer</span>
                    <p>
                      {selectedDocument.issuer?.name} ·{" "}
                      {selectedDocument.issuer?.designation}
                    </p>
                    <p>{selectedDocument.issuer?.organization}</p>
                    <p>{selectedDocument.issuer?.ministry}</p>
                  </div>

                  <div className="document-modal-actions">
                    <button
                      type="button"
                      className="btn-primary flex-1"
                      onClick={() =>
                        downloadDocument(
                          selectedDocument.downloadPath,
                          selectedDocument.downloadName,
                        )
                      }
                    >
                      <span>Download</span>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary flex-1"
                      onClick={() => openPreview(selectedDocument.previewImage)}
                    >
                      <span>Open Preview</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Achievements;
